/**
 * Stable resolve-methods request construction + effect coordination.
 * Keeps network effects keyed by primitive signatures, not array identity.
 */

import {
  buildResolveMethodsCacheKey,
  detectCollectionSignals,
} from './normalizeDisputeMethod';
import {
  calculateUniqueLateMonths,
  hasChargeOffInPaymentHistory,
} from './disputeEligibility';

/**
 * Normalize money for resolve-methods payloads.
 * Accepts "$2,343.00", "2,343.00", 2343, null, "", "-", "N/A".
 */
export function normalizeMoneyAmount(value) {
  if (value == null || value === false) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (/^(n\/a|na|-|--|none|null|nil|\.)$/i.test(text)) return null;
  const cleaned = text.replace(/[$,\s]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildOpenAccountsList(tradelines = []) {
  return (tradelines || [])
    .filter((acc) => {
      const status = (acc.status || acc.account_status || '').toLowerCase();
      const accountType = (acc.account_type || acc.type || '').toLowerCase();
      const isOpen = status.includes('open')
        || (!status.includes('closed') && !status.includes('chargeoff'));
      const isRevolving = accountType.includes('revolving')
        || accountType.includes('credit')
        || accountType.includes('card');
      return isOpen && isRevolving;
    })
    .map((acc) => ({
      item_id: acc.id || acc.account_id || acc.tradeline_id,
      creditor_name: acc.creditor_name || acc.account_name || acc.creditorName || '',
      account_type: acc.account_type || acc.type || '',
      account_status: acc.status || acc.account_status || 'open',
      status: acc.status || acc.account_status || 'open',
      date_opened: acc.date_opened || acc.open_date || '',
      bureau: acc.bureau,
      bureaus_reported: acc.bureaus_reported || acc.bureaus || (acc.bureau ? [acc.bureau] : []),
    }));
}

export function buildItemsForResolution({
  proceedingSelectedItems = { accounts: [], inquiries: [], personalInfo: [] },
  negativeAccounts = [],
  hardInquiries = [],
  personalInfoItems = [],
} = {}) {
  const accountById = new Map(
    (negativeAccounts || []).map((item) => [String(item.id), item])
  );
  const inquiryById = new Map(
    (hardInquiries || []).map((item) => [String(item.id), item])
  );
  const personalInfoById = new Map(
    (personalInfoItems || []).map((item) => [String(item.id), item])
  );

  const itemsForResolution = [];

  (proceedingSelectedItems.accounts || []).forEach((id) => {
    const account = accountById.get(String(id));
    if (!account) return;
    const uniqueLateCount = calculateUniqueLateMonths(account);
    const paymentStatus = String(
      account.payment_status
      || account.paymentStatus
      || account.Pay_Status
      || ''
    );
    const rawStatus = String(account.status || account.account_status || '');
    const statusBlob = `${rawStatus} ${paymentStatus} ${account.account_type || ''}`.toLowerCase();
    const hasChargeOff = hasChargeOffInPaymentHistory(account)
      || statusBlob.includes('chargeoff')
      || statusBlob.includes('charge-off')
      || statusBlob.includes('charge off')
      || statusBlob.includes('charged off')
      || String(account.current_rating || '').toLowerCase().includes('charge')
      || String(account.highest_adverse_rating || '').toLowerCase().includes('charge')
      || String(account.most_recent_adverse_rating || '').toLowerCase().includes('charge');
    const collectionSignals = detectCollectionSignals(account);
    const bureauComparisonTypeDetail = account.bureau_comparison?.account_type_detail || {};
    const extractedTypeDetail = collectionSignals.account_type_detail
      || account.account_type_detail
      || Object.values(bureauComparisonTypeDetail).find((v) => v)
      || '';
    const pastDue = normalizeMoneyAmount(
      account.past_due ?? account.pastDue ?? account.amount_past_due ?? null
    );
    const balance = normalizeMoneyAmount(
      account.balance ?? account.unpaid_balance ?? account.current_balance ?? null
    );
    const remarks = [
      account.remarks,
      account.comments,
      account.comment,
      account.special_comments,
    ].filter(Boolean).join(' ');

    itemsForResolution.push({
      item_id: id,
      item_type: 'account',
      account_type: account.account_type || account.type || 'standard',
      account_type_detail: extractedTypeDetail,
      account_status: rawStatus || (account.status?.toLowerCase()?.includes('open') ? 'open' : 'closed'),
      status: rawStatus || account.status || account.account_status || '',
      payment_status: paymentStatus || undefined,
      past_due: pastDue,
      balance: balance,
      unpaid_balance: balance,
      current_rating: account.current_rating || account.currentRating || undefined,
      highest_adverse_rating:
        account.highest_adverse_rating || account.highestAdverseRating || undefined,
      most_recent_adverse_rating:
        account.most_recent_adverse_rating || account.mostRecentAdverseRating || undefined,
      remarks: remarks || undefined,
      comments: remarks || undefined,
      bureau: account.bureau,
      bureaus: account.bureaus_reported || account.bureaus || (account.bureau ? [account.bureau] : []),
      bureaus_reported: account.bureaus_reported || account.bureaus || (account.bureau ? [account.bureau] : []),
      late_payment_count: uniqueLateCount,
      is_chargeoff: hasChargeOff,
      is_collection: Boolean(collectionSignals.is_collection),
      creditor_name: account.creditor_name || account.account_name || account.creditorName || '',
      original_creditor:
        collectionSignals.original_creditor
        || account.original_creditor
        || account.originalCreditor
        || null,
      collection_agency:
        account.collection_agency || account.collector_name || account.collectorName || null,
      dispute_family: collectionSignals.is_collection ? 'collection' : undefined,
      method: collectionSignals.is_collection ? 'debt_validation' : undefined,
    });
  });

  (proceedingSelectedItems.inquiries || []).forEach((id) => {
    const inquiry = inquiryById.get(String(id));
    if (!inquiry) return;
    itemsForResolution.push({
      item_id: id,
      item_type: 'inquiry',
      bureau: inquiry.bureau,
      bureaus: inquiry.bureau ? [inquiry.bureau] : [],
      bureaus_reported: inquiry.bureau ? [inquiry.bureau] : [],
      creditor_name:
        inquiry.creditor_name
        || inquiry.company_name
        || inquiry.inquiry_name
        || inquiry.creditor
        || '',
      inquiry_date: inquiry.inquiry_date || inquiry.date || '',
      is_legitimate: false,
    });
  });

  (proceedingSelectedItems.personalInfo || []).forEach((id) => {
    const piItem = personalInfoById.get(String(id));
    if (!piItem) return;
    itemsForResolution.push({
      item_id: id,
      item_type: 'personal_info',
      field_name: piItem.field || piItem.fieldLabel || String(id).replace('pi-', ''),
      bureaus: piItem.bureaus || [],
      bureaus_reported: piItem.bureaus || [],
    });
  });

  return itemsForResolution;
}

export function buildResolveMethodsRequestInput({
  proceedingSelectedItems,
  negativeAccounts,
  hardInquiries,
  personalInfoItems,
  tradelines,
  resolvedRoundNumber = 1,
  mode = 'factual',
} = {}) {
  const itemsForResolution = buildItemsForResolution({
    proceedingSelectedItems,
    negativeAccounts,
    hardInquiries,
    personalInfoItems,
  });
  const openAccountsList = buildOpenAccountsList(tradelines);
  const resolveMethodsKey = buildResolveMethodsCacheKey({
    round: resolvedRoundNumber,
    mode,
    items: itemsForResolution.map((item) => ({
      id: item.item_id,
      disputeFamily: item.dispute_family || null,
      method: item.method || null,
      accountType: item.account_type_detail || null,
      isCollection: Boolean(item.is_collection),
      isChargeoff: Boolean(item.is_chargeoff),
      originalCreditor: item.original_creditor || null,
    })),
  });

  return {
    itemsForResolution,
    openAccountsList,
    resolveMethodsKey,
    round_number: resolvedRoundNumber,
    mode,
  };
}

/**
 * Coordinate resolve-methods debounce / abort / StrictMode behavior.
 * Used by ItemSelectionPage and unit tests.
 */
export function createResolveMethodsRequestCoordinator({
  debounceMs = 300,
  fetchImpl = fetch,
  endpoint,
  getAuthToken,
  onLoading,
  onSuccess,
  onError,
  onEmpty,
} = {}) {
  const state = {
    lastCompletedKey: '',
    inFlightKey: '',
    abortController: null,
    lastSuccessful: null,
  };

  function shouldSkipForStableKey(key) {
    return Boolean(key) && (
      key === state.lastCompletedKey
      || key === state.inFlightKey
    );
  }

  function start({ key, body, signal: externalSignal } = {}) {
    if (!key || !body?.itemsForResolution?.length) {
      state.lastCompletedKey = '';
      state.inFlightKey = '';
      if (onEmpty) onEmpty();
      if (onLoading) onLoading(false);
      return { started: false, reason: 'empty' };
    }

    if (key === state.lastCompletedKey) {
      if (onSuccess && state.lastSuccessful) onSuccess(state.lastSuccessful, { reused: true, key });
      if (onLoading) onLoading(false);
      return { started: false, reason: 'already_completed' };
    }

    // Same key in flight — do not abort / restart (StrictMode + equivalent rerenders)
    if (key === state.inFlightKey) {
      return { started: false, reason: 'already_in_flight' };
    }

    // Genuine key change — abort previous in-flight request
    if (state.abortController && state.inFlightKey && state.inFlightKey !== key) {
      state.abortController.abort();
    }

    const abortController = new AbortController();
    state.abortController = abortController;
    state.inFlightKey = key;
    if (onLoading) onLoading(true);

    let fetchStarted = false;
    const timeoutId = setTimeout(async () => {
      fetchStarted = true;
      try {
        if (externalSignal?.aborted || abortController.signal.aborted) return;
        const token = getAuthToken ? getAuthToken() : null;
        const response = await fetchImpl(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            items: body.itemsForResolution,
            round_number: body.round_number,
            open_accounts: body.openAccountsList,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to resolve methods');
        }

        const data = await response.json();
        if (abortController.signal.aborted) return;
        if (state.inFlightKey !== key) return;

        if (data.success) {
          state.lastCompletedKey = key;
          state.lastSuccessful = data;
          if (onSuccess) onSuccess(data, { reused: false, key });
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (onError) onError(error, { key, lastSuccessful: state.lastSuccessful });
      } finally {
        if (state.inFlightKey === key) {
          state.inFlightKey = '';
          if (onLoading) onLoading(false);
        }
      }
    }, debounceMs);

    return {
      started: true,
      reason: 'scheduled',
      abortController,
      cancelDebounce: () => clearTimeout(timeoutId),
      // Cleanup for effect unmount / StrictMode:
      // clear debounce only. If fetch has not started, release inFlight so remount
      // can schedule the same key once. Abort only happens on genuine key change.
      cleanup: () => {
        clearTimeout(timeoutId);
        if (!fetchStarted && state.inFlightKey === key) {
          state.inFlightKey = '';
          if (onLoading) onLoading(false);
        }
      },
    };
  }

  function getState() {
    return { ...state };
  }

  return {
    start,
    shouldSkipForStableKey,
    getState,
  };
}
